"use client";

import { useQuery } from "@apollo/client";
import {
  LiquidPill,
  PageEmptyStateV2,
  PageMetaBarV2,
} from "components/ui/v2";
import { gqlEndorsement } from "gql";
import {
  type IGetUserEndorsementResultsInput,
  type IGetUserEndorsementsResponse,
} from "gql/Endorsement/queries";
import { ListChecks } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import { useEffect, useState } from "react";
import type { IUserEndorsementResult } from "types/Endorsement.type";
import type { ISkill } from "types/Skills.type";
import { EndorsementSkillCardSkeletonV2 } from "./EndorsementSkillCardSkeletonV2";
import { EndorsementSkillListV2 } from "./EndorsementSkillListV2";

export interface PublicProfileEndorsementTabV2Props {
  userId: string;
  profileUserName: string;
  isOwnProfile: boolean;
  canShare: boolean;
  onReview: (skill: ISkill) => void;
  onValidate: (skillId: string, skillName: string) => void;
  onShare: (skill: ISkill) => void;
  onCountChange?: (count: number) => void;
}

const INITIAL_VISIBLE = 5;

export function PublicProfileEndorsementTabV2({
  userId,
  profileUserName,
  isOwnProfile,
  canShare,
  onReview,
  onValidate,
  onShare,
  onCountChange,
}: PublicProfileEndorsementTabV2Props) {
  const { t } = useTranslation("common");
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const [endorsements, setEndorsements] = useState<IUserEndorsementResult[]>();

  const { data, loading } = useQuery<
    IGetUserEndorsementsResponse,
    { getUserEndorsementResultsInput: IGetUserEndorsementResultsInput }
  >(gqlEndorsement.queries.GET_USER_ENDORSEMENTS_RESULTS, {
    variables: {
      getUserEndorsementResultsInput: { userId },
    },
    fetchPolicy: "network-only",
    onCompleted: ({ userEndorsements }) => {
      setEndorsements(userEndorsements);
      onCountChange?.(userEndorsements?.length ?? 0);
    },
  });

  useEffect(() => {
    setEndorsements(data?.userEndorsements);
    onCountChange?.(data?.userEndorsements?.length ?? 0);
  }, [data?.userEndorsements, onCountChange]);

  useEffect(() => {
    if (isOwnProfile && endorsements) {
      setVisibleCount(endorsements.length);
    }
  }, [isOwnProfile, endorsements]);

  if (loading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <EndorsementSkillCardSkeletonV2 key={index} />
        ))}
      </div>
    );
  }

  if (!endorsements?.length) {
    return (
      <PageEmptyStateV2
        variant="empty"
        title={
          isOwnProfile
            ? t("No tienes habilidades registradas")
            : t("No tiene habilidades registradas")
        }
        description={
          isOwnProfile
            ? t(
                "Agrega tus habilidades y pide validaciones de tus colegas para mostrar tu experiencia"
              )
            : t("Este usuario aún no ha registrado sus habilidades profesionales")
        }
      />
    );
  }

  const visibleItems = isOwnProfile
    ? endorsements
    : endorsements.slice(0, visibleCount);
  const totalValidations = endorsements.reduce((sum, item) => sum + item.times, 0);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
          {t("Endorsements")}
        </h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-zinc-400">
          {t("Aquí puedes valorar las habilidades de")} {profileUserName}
        </p>
      </div>

      <PageMetaBarV2
        icon={ListChecks}
        summary={
          <>
            {t("Mostrando")}{" "}
            <span className="font-semibold text-current">{visibleItems.length}</span>{" "}
            {t("de")}{" "}
            <span className="font-semibold text-current">{endorsements.length}</span>{" "}
            {t("habilidades")}
          </>
        }
      />

      <EndorsementSkillListV2
        items={visibleItems}
        userId={userId}
        isOwnProfile={isOwnProfile}
        canShare={canShare}
        onReview={onReview}
        onValidate={!isOwnProfile ? onValidate : undefined}
        onShare={onShare}
      />

      {!isOwnProfile && endorsements.length > INITIAL_VISIBLE ? (
        <div className="flex flex-wrap items-center justify-center gap-2">
          {visibleCount < endorsements.length ? (
            <LiquidPill
              as="button"
              type="button"
              variant="sky"
              appearance="tinted"
              size="sm"
              text={t("Ver más")}
              onClick={() => setVisibleCount((current) => current + INITIAL_VISIBLE)}
            />
          ) : null}
          {visibleCount > INITIAL_VISIBLE ? (
            <LiquidPill
              as="button"
              type="button"
              variant="slate"
              appearance="tinted"
              size="sm"
              text={t("Ver menos")}
              onClick={() => setVisibleCount(INITIAL_VISIBLE)}
            />
          ) : null}
        </div>
      ) : null}

      {!isOwnProfile ? (
        <p className="text-xs text-slate-500 dark:text-zinc-400">
          {t("Mostrando")}{" "}
          {visibleCount < endorsements.length ? visibleCount : endorsements.length}{" "}
          {t("de")} {endorsements.length} {t("validaciones")} ·{" "}
          <span className="font-semibold">{totalValidations}</span> {t("Validaciones")}
        </p>
      ) : null}
    </div>
  );
}
